import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelectedAccountId } from '../../hooks/useSubscription'
import { api } from '../../services/api'
import CancelAddonModal from './CancelAddonModal'
import './AddonOption.css'

/**
 * Individual add-on option card component
 * Displays add-on pricing, features, and subscription status
 *
 * Phase 5: Add-on subscription UI for account-level add-ons
 * Phase 6: Improved UX with branded cancel modal
 */
const AddonOption = ({ addon, isActive, subscriptionData, onSubscriptionChange, disabled }) => {
  const { t } = useTranslation('subscription')
  const accountId = useSelectedAccountId()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancellationResult, setCancellationResult] = useState(null)

  // Build cancellation info from props (persisted) or local state (just cancelled)
  const cancellationInfo = useMemo(() => {
    // Check if addon is pending cancellation from backend data
    if (subscriptionData?.cancel_at_period_end && subscriptionData?.expires_at) {
      const expiresAt = new Date(subscriptionData.expires_at)
      return {
        expiresAt,
        formattedDate: expiresAt.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
    }
    return null
  }, [subscriptionData])

  // Translation helper for add-on details
  const getTranslatedAddonDetail = (field) => {
    const key = `page.addons.items.${addon.id}.${field}`
    const translated = t(key, { defaultValue: null })
    return translated !== key ? translated : addon[field]
  }

  // Translation helper for features
  const getTranslatedFeature = (index) => {
    const key = `page.addons.items.${addon.id}.feature${index}`
    const translated = t(key, { defaultValue: null })
    return translated !== key ? translated : (addon.features && addon.features[index])
  }

  const handleSubscribe = async () => {
    if (!accountId) {
      setError(t('page.addons.errorNoAccount', { defaultValue: 'Account context required' }))
      return
    }

    try {
      setIsProcessing(true)
      setError(null)

      // Create checkout session for the add-on
      const { url } = await api.createAddonCheckoutSession(accountId, addon.id)

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      console.error('Failed to create add-on checkout session:', err)
      setError(err.message || t('page.addons.errorCheckout', { defaultValue: 'Failed to start checkout' }))
      setIsProcessing(false)
    }
  }

  // Open the cancel modal instead of using window.confirm
  const handleUnsubscribe = () => {
    if (!accountId) {
      setError(t('page.addons.errorNoAccount', { defaultValue: 'Account context required' }))
      return
    }
    setShowCancelModal(true)
  }

  // Handle modal cancel/close
  const handleCancelModalClose = () => {
    setShowCancelModal(false)
    // Clear local cancellation result (parent already has the data via onSubscriptionChange)
    if (cancellationResult) {
      setCancellationResult(null)
    }
  }

  // Handle confirmed cancellation
  const handleConfirmCancel = async () => {
    try {
      setIsProcessing(true)
      setError(null)

      // Cancel the add-on subscription
      const result = await api.removeAddon(accountId, addon.id)

      // Store result for the success state in modal
      setCancellationResult(result)
      setIsProcessing(false)

      // Notify parent component (pass cancellation info)
      if (onSubscriptionChange) {
        onSubscriptionChange(addon.id, false, result)
      }
    } catch (err) {
      console.error('Failed to remove add-on:', err)
      setError(err.message || t('page.addons.errorRemove', { defaultValue: 'Failed to remove add-on' }))
      setIsProcessing(false)
      setShowCancelModal(false)
    }
  }

  // Determine the visual state of the addon
  const isCancelled = isActive && cancellationInfo
  const cardClassName = `addon-option ${isActive ? 'active' : ''} ${isCancelled ? 'cancelled' : ''}`

  return (
    <div className={cardClassName}>
      {/* Badge - Active or Cancelled */}
      {isActive && (
        <div className={`addon-active-badge ${isCancelled ? 'cancelled' : ''}`}>
          {isCancelled
            ? t('page.addons.cancelled', { defaultValue: 'Cancelled' })
            : t('page.addons.active', { defaultValue: 'Active' })
          }
        </div>
      )}

      {/* Add-on Header */}
      <div className="addon-header">
        <h3 className="addon-name">{getTranslatedAddonDetail('name')}</h3>
        <span className="addon-price">
          {t('page.addons.priceFormat', {
            price: addon.price_monthly,
            defaultValue: `€${addon.price_monthly}/month`
          })}
        </span>
      </div>

      {/* Description */}
      <p className="addon-description">
        {getTranslatedAddonDetail('description')}
      </p>

      {/* Features List */}
      {addon.features && addon.features.length > 0 && (
        <div className="addon-features">
          <ul>
            {addon.features.map((feature, index) => (
              <li key={index} className="feature-item">
                <span className="feature-icon">{t('planCard.features.checkmark', { defaultValue: '✓' })}</span>
                <span className="feature-text">{getTranslatedFeature(index)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cancellation Status Banner */}
      {cancellationInfo && (
        <div className="addon-cancellation-banner">
          <div className="cancellation-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="cancellation-content">
            <p className="cancellation-label">
              {t('page.addons.expiresLabel', { defaultValue: 'Active until' })}
            </p>
            <p className="cancellation-date">{cancellationInfo.formattedDate}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="addon-error">
          <p>{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="addon-footer">
        {isCancelled ? (
          /* Cancelled state - show resubscribe option */
          <button
            className="addon-button subscribe"
            onClick={handleSubscribe}
            disabled={disabled || isProcessing}
          >
            {isProcessing
              ? t('page.addons.subscribingButton', { defaultValue: 'Processing...' })
              : t('page.addons.resubscribeButton', { defaultValue: 'Resubscribe' })
            }
          </button>
        ) : isActive ? (
          /* Active state - show remove option */
          <button
            className="addon-button remove"
            onClick={handleUnsubscribe}
            disabled={disabled || isProcessing}
          >
            {isProcessing
              ? t('page.addons.removingButton', { defaultValue: 'Removing...' })
              : t('page.addons.removeButton', { defaultValue: 'Remove' })
            }
          </button>
        ) : (
          /* Inactive state - show subscribe option */
          <button
            className="addon-button subscribe"
            onClick={handleSubscribe}
            disabled={disabled || isProcessing}
          >
            {isProcessing
              ? t('page.addons.subscribingButton', { defaultValue: 'Processing...' })
              : t('page.addons.subscribeButton', { defaultValue: 'Subscribe' })
            }
          </button>
        )}
      </div>

      {/* Cancel Add-on Modal */}
      <CancelAddonModal
        isOpen={showCancelModal}
        addon={addon}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelModalClose}
        isProcessing={isProcessing}
        cancellationResult={cancellationResult}
      />
    </div>
  )
}

export default AddonOption
